import React, { useEffect, useState } from 'react';

import Row from '@amzn/meridian/row';
import DataTable, {
  DataTableBody,
  DataTableHeader,
  DataTableCell,
  DataTableRow,
  DataTableHead
} from '@amzn/meridian/data-table';
import { Button, ButtonGroup, ButtonOption, Column, Icon, Input, Text } from '@amzn/meridian';
import caretUpTokens from '@amzn/meridian-tokens/base/icon/caret-up';
import caretDownTokens from '@amzn/meridian-tokens/base/icon/caret-down';
import chevronRightSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-right-small';
import chevronDownSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-down-small';

import moment from 'moment';
import { chainWalk, combineDynamicShiftPlanBetaTableData, convertToFixedDP } from '../../utils/helpers';
import { processMapping } from '../../utils/constants';
import { AGGREGATE_COLUMNS_CONFIG } from './dynamicShiftPlanTableConfig';

const convertToString = (val, dp) => {
  return val === '-' || isNaN(val) ? '-' : convertToFixedDP(val, dp).toLocaleString();
};

const CELL_COLOR_UP = '#ebf5ff';
const CELL_COLOR_DOWN = '#ffdbdb';
const CARET_COLOR_UP = '#0085b3';
const CARET_COLOR_DOWN = '#cc0c39';
const TABLE_LABEL_COL_SPAN = 2;

const DynamicShiftPlanBetaTable = (props) => {
  const { graphedMetric, adherenceGraphData } = props;

  // Get aggregate configuration for current metric
  const aggregateConfig = AGGREGATE_COLUMNS_CONFIG[graphedMetric] || AGGREGATE_COLUMNS_CONFIG.default;

  const [graphPeriodMinutes, setGraphPeriodMinutes] = useState(15);
  const [expandedStates, setExpandedStates] = useState(
    aggregateConfig.reduce((acc, config) => {
      acc[config.key] = config.defaultExpanded || false;
      return acc;
    }, {})
  );

  useEffect(() => {
    setGraphPeriodMinutes(15);
    // Reset expanded states when metric changes
    setExpandedStates(
      aggregateConfig.reduce((acc, config) => {
        acc[config.key] = config.defaultExpanded || false;
        return acc;
      }, {})
    );
  }, [graphedMetric]);

  const processDisplayNames = {
    overall: 'Overall',
    sortation: 'Sort',
    outbound: 'Pick Stage'
  };

  let tableData = {};
  const metricData = adherenceGraphData[graphedMetric] || {};

  Object.entries(metricData).forEach(([processKey, processData]) => {
    const displayName = processDisplayNames[processKey] || processKey;
    tableData[displayName] = {
      planned: processData.planned || [],
      actual: processData.actual || []
    };
  });
  function averageDataByPeriod(data, periodMinutes) {
    if (!data || data.length === 0) return [];

    const periodSeconds = periodMinutes * 60;
    const grouped = {};

    data.forEach((datum) => {
      const periodStart = Math.ceil(datum.endInSeconds / periodSeconds) * periodSeconds;
      if (!grouped[periodStart]) grouped[periodStart] = [];
      grouped[periodStart].push(datum.value);
    });

    // Handle scenarios where the last data interval is xx:15
    // and our periodMinutes is non-15m.
    const lastTimeInterval = data[data.length - 1].endInSeconds;
    grouped[Math.ceil(lastTimeInterval / periodSeconds) * periodSeconds] = [data[data.length - 1].value];

    return Object.entries(grouped).map(([endInSeconds, values]) => ({
      endInSeconds: parseInt(endInSeconds),
      value:
        graphedMetric === 'volume'
          ? values[0] // For volume, use the first data point from the time interval instead of averaging since it's a cumulative metric.
          : values.reduce((sum, val) => sum + val, 0) / values.length
    }));
  }

  function createTable(tableData) {
    const averagedData = {};
    Object.entries(tableData).forEach(([key, value]) => {
      averagedData[key] = {
        planned: averageDataByPeriod(value.planned, graphPeriodMinutes),
        actual: averageDataByPeriod(value.actual, graphPeriodMinutes)
      };
    });

    // The data we get from the backend is terminologically correct since
    // planning systems and actual systems use different terms, but the
    // user gets confused.
    if ('Label' in averagedData) {
      if (!('Inducted' in averagedData)) {
        averagedData['Inducted'] = {
          planned: [],
          actual: []
        };
      }
      averagedData['Inducted'].planned = averagedData['Label'].planned;
    }
    if ('Automated Inducted' in averagedData && 'Label' in averagedData) {
      averagedData['Automated Inducted'].planned = averagedData['Label'].planned;
    }
    if (graphedMetric === 'rate' && 'Induct' in averagedData && 'Label' in averagedData) {
      averagedData['Induct'].planned = averagedData['Label'].planned;
      delete averagedData['Label'];
    }
    if ('Rostered' in averagedData && 'Overall' in averagedData) {
      averagedData['Rostered'].actual = averagedData['Overall'].actual;
    }
    if (graphedMetric === 'headcount' || graphedMetric === 'volume') {
      combineDynamicShiftPlanBetaTableData(averagedData, ['ADTA Container Building', 'Container Building'], 'Stowed', [
        'planned'
      ]);
    }
    if (graphedMetric === 'headcount') {
      combineDynamicShiftPlanBetaTableData(averagedData, ['Inducted', 'Stowed'], 'Sortation', ['planned']);
      combineDynamicShiftPlanBetaTableData(averagedData, ['UTR', 'Site Support'], 'other', ['actual']);

      // Aggregate Pick Stage data from processMapping
      const pickStageProcesses = processMapping['Pick Stage'] || [];
      const aggregatedPlannedMap = new Map();
      const aggregatedActualMap = new Map();

      pickStageProcesses.forEach((processKey) => {
        const processData = averagedData[processKey];
        if (processData) {
          processData.planned.forEach((datum) => {
            const timeKey = datum.endInSeconds;
            if (!aggregatedPlannedMap.has(timeKey)) {
              aggregatedPlannedMap.set(timeKey, { endInSeconds: timeKey, value: 0 });
            }
            aggregatedPlannedMap.get(timeKey).value += datum.value;
          });
          processData.actual.forEach((datum) => {
            const timeKey = datum.endInSeconds;
            if (!aggregatedActualMap.has(timeKey)) {
              aggregatedActualMap.set(timeKey, { endInSeconds: timeKey, value: 0 });
            }
            aggregatedActualMap.get(timeKey).value += datum.value;
          });
        }
      });

      const aggregatedPlanned = Array.from(aggregatedPlannedMap.values()).sort(
        (a, b) => a.endInSeconds - b.endInSeconds
      );
      const aggregatedActual = Array.from(aggregatedActualMap.values()).sort((a, b) => a.endInSeconds - b.endInSeconds);
      averagedData['Pick Stage'] = {
        planned: aggregatedPlanned,
        actual: aggregatedActual
      };
    }

    if (!props.isDynamicShiftPlanBetaEnabled) {
      Object.entries(averagedData).forEach(([key, value]) => {
        if (graphedMetric !== 'headcount' || (key !== 'Overall' && key !== 'Rostered')) {
          value['planned'] = [];
        }
      });
    }

    let times = new Set();
    if (Object.keys(averagedData).length !== 0) {
      Object.values(averagedData).forEach((datum) => {
        datum['actual'].forEach((datum) => times.add(datum.endInSeconds));
        datum['planned'].forEach((datum) => times.add(datum.endInSeconds));
      });
    }
    times = Array.from(times)
      .filter((item) => item != null)
      .sort((a, b) => a - b);

    // Create aggregate data based on configuration
    const aggregateData = {};
    aggregateConfig.forEach((config) => {
      // Try to find data by key first, then by display name
      aggregateData[config.key] =
        averagedData[config.key] ||
        averagedData[config.displayName] ||
        averagedData['Sortation'] ||
        averagedData['Outbound'];
    });

    const sortedEntries = Object.entries(averagedData)
      .filter(([key]) => {
        return (
          key !== 'Sortation' &&
          key !== 'Outbound' &&
          !aggregateConfig.some((config) => config.key === key || config.displayName === key)
        );
      })
      .filter(([_, datum]) => {
        // Remove empty values.
        return datum.planned.length !== 0 || datum.actual.length !== 0;
      })
      .sort(([keyA], [keyB]) => {
        return keyA.localeCompare(keyB);
      });

    // Group processes by aggregate configuration
    const processGroups = {};
    aggregateConfig.forEach((config) => {
      processGroups[config.key] = sortedEntries.filter(([key]) => {
        return config.processes.some((process) => process.key === key);
      });
    });

    const headerRow = (
      <DataTableRow>
        <DataTableHeader />
        {aggregateConfig.map((config) => {
          const colSpan = config.showPlannedVsActual ? TABLE_LABEL_COL_SPAN : 1;
          return (
            <DataTableHeader
              key={config.key}
              colSpan={expandedStates[config.key] ? colSpan * processGroups[config.key].length + colSpan : colSpan}>
              <Button
                type="link"
                onClick={() => {
                  setExpandedStates((prev) => ({
                    ...prev,
                    [config.key]: !prev[config.key]
                  }));
                }}>
                <Icon
                  tokens={expandedStates[config.key] ? chevronDownSmallTokens : chevronRightSmallTokens}
                  color="secondary"
                />
                <Text type="h50">{config.displayName}</Text>
              </Button>
            </DataTableHeader>
          );
        })}
      </DataTableRow>
    );
    const headerProcessRow = (
      <DataTableRow>
        <DataTableHeader />
        {aggregateConfig.map((config) => {
          const colSpan = config.showPlannedVsActual ? TABLE_LABEL_COL_SPAN : 1;
          return (
            <React.Fragment key={config.key}>
              <DataTableHeader textAlign="end" colSpan={colSpan}>
                Total
              </DataTableHeader>
              {expandedStates[config.key] &&
                processGroups[config.key].map(([key]) => {
                  // Find the corresponding process object to get the displayName
                  const processObj = config.processes.find((process) => process.key === key);
                  const displayName = processObj ? processObj.displayName : key;
                  return (
                    <DataTableHeader key={key} textAlign="end" colSpan={colSpan}>
                      {displayName}
                    </DataTableHeader>
                  );
                })}
            </React.Fragment>
          );
        })}
      </DataTableRow>
    );

    const headerLabelRow = (
      <DataTableRow>
        <DataTableHeader />
        {aggregateConfig
          .map((config) => {
            const processCount = expandedStates[config.key] ? processGroups[config.key].length + 1 : 1;
            return new Array(processCount).fill(null).map((_, index) =>
              config.showPlannedVsActual
                ? [
                    <DataTableHeader key={`${config.key}-${index}-pva`} textAlign="end">
                      PvA
                    </DataTableHeader>,
                    <DataTableHeader key={`${config.key}-${index}-delta`} textAlign="end">
                      Delta
                    </DataTableHeader>
                  ]
                : [
                    <DataTableHeader key={`${config.key}-${index}-actual`} textAlign="end">
                      Actual
                    </DataTableHeader>
                  ]
            );
          })
          .flat()}
      </DataTableRow>
    );

    function createTableCells(cellData, time, config) {
      const actualValue = chainWalk(() => cellData.actual.filter((datum) => datum.endInSeconds === time)[0].value, '-');

      if (!config.showPlannedVsActual) {
        return [<DataTableCell textAlign="end">{convertToString(actualValue, 0)}</DataTableCell>];
      }

      const plannedValue = chainWalk(
        () => cellData.planned.filter((datum) => datum.endInSeconds === time)[0].value,
        '-'
      );

      const delta = actualValue - plannedValue;

      let cellColor = 'primary';
      let adherencePrefixElement = <div />;
      if (delta < 0) {
        cellColor = CELL_COLOR_DOWN;
        adherencePrefixElement = (
          <div style={{ color: CARET_COLOR_DOWN }}>
            <Icon tokens={caretDownTokens} />
          </div>
        );
      } else if (delta > 0) {
        cellColor = CELL_COLOR_UP;
        adherencePrefixElement = (
          <div style={{ color: CARET_COLOR_UP }}>
            <Icon tokens={caretUpTokens} />
          </div>
        );
      }

      return [
        <DataTableCell textAlign="end">
          {convertToString(plannedValue, 1) + '/' + convertToString(actualValue, 0)}
        </DataTableCell>,
        <DataTableCell textAlign="end" backgroundColor={cellColor}>
          <Row alignmentHorizontal="justify">
            {adherencePrefixElement}
            {convertToString(delta, 0)}
          </Row>
        </DataTableCell>
      ];
    }

    const tableRows = [];
    times.forEach((time) => {
      const timeLabel = moment(new Date(time * 1000))
        .utc()
        .format('HH:mm');
      const tableCells = [];
      aggregateConfig.forEach((config) => {
        const configAggregateData = aggregateData[config.key];
        if (configAggregateData) {
          tableCells.push(...createTableCells(configAggregateData, time, config));
        }

        if (expandedStates[config.key]) {
          processGroups[config.key].forEach(([_, value]) => {
            tableCells.push(...createTableCells(value, time, config));
          });
        }
      });

      const row = (
        <DataTableRow highlightOnHover={true}>
          <DataTableCell>
            <Text type="h50">{timeLabel}</Text>
          </DataTableCell>
          {tableCells}
        </DataTableRow>
      );

      tableRows.push(row);
    });

    return (
      <DataTable
        size="small"
        headerRows={1}
        headerColumns={1}
        stickyHeaderColumn={true}
        showColumnDividers={true}
        layout="auto">
        <DataTableHead>
          {headerRow}
          {headerProcessRow}
          {headerLabelRow}
        </DataTableHead>
        <DataTableBody>{tableRows}</DataTableBody>
      </DataTable>
    );
  }

  return (
    <Column width="100%" spacing="300">
      <Row alignmentHorizontal="center">
        <ButtonGroup size="small" type="outline" value={graphPeriodMinutes} onChange={setGraphPeriodMinutes}>
          <ButtonOption value={15}>15 min</ButtonOption>
          <ButtonOption value={30}>30 min</ButtonOption>
          <ButtonOption value={60}>1 hr</ButtonOption>
        </ButtonGroup>
      </Row>
      <Column overflowX="auto">{createTable(tableData)}</Column>
    </Column>
  );
};
export default DynamicShiftPlanBetaTable;
